<template>
  <div class="fr" :class="{ 'fr--bare': !label }" data-test="settings-row">
    <div v-if="label" class="fr__label" :class="{ 'fr__label--choice': choice }">
      <label v-if="forId" :for="forId" class="fr__name">{{ label }}</label>
      <div v-else class="fr__name">{{ label }}</div>
      <div v-if="help" class="fr__help">{{ help }}</div>
    </div>

    <div class="fr__field">
      <slot />
      <div
        v-if="note || noteCodes.length || $slots.note"
        class="fr__note"
        :class="noteTone ? `fr__note--${noteTone}` : ''"
        data-test="settings-note"
      >
        <WfIcon :name="noteTone ? 'alert' : 'info'" :size="14" class="fr__note-icon" />
        <div class="fr__note-body">
          <slot name="note">{{ note }}</slot>
          <div v-if="$slots['note-extra']" class="fr__note-extra" data-test="settings-note-extra">
            <slot name="note-extra" />
          </div>
          <div v-if="noteCodes.length" class="fr__codes">
            <code v-for="code in noteCodes" :key="code" class="fr__code">{{ code }}</code>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * One row of a settings card (design frames 1g-1p): the label, and under it a help line, on the left;
 * the field on the right. A row without a label lets the field take the whole width. Under the field a
 * note can sit: grey (info), amber or red, with an optional list of code-styled tokens (placeholders).
 * The `note-extra` slot sits inside the note, under its text, for a small follow-up (a sentence or a text
 * button); it draws nothing, and no wrapper, while the slot is empty.
 *
 * Presentational. Below 620px (the sheet is the full viewport width there) the label stacks over the field.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import WfIcon from '../WfIcon.vue';

export default {
  name: 'FieldRow',
  components: { WfIcon },
  props: {
    label: { type: String, default: '' },
    help: { type: String, default: '' },
    /** The id of the control, so the label is a real <label for>. */
    forId: { type: String, default: '' },
    /** Radio / checkbox cards: the label sits a pixel lower to line up with the first card. */
    choice: { type: Boolean, default: false },
    note: { type: String, default: '' },
    /** '' (grey, info icon) | 'amber' | 'red' */
    noteTone: { type: String, default: '', validator: v => ['', 'amber', 'red'].includes(v) },
    noteCodes: { type: Array, default: () => [] },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.fr {
  display: grid;
  grid-template-columns: $wf-label-col minmax(0, 1fr);
  gap: 6px 20px;
  align-items: start;
  min-width: 0;

  &--bare { grid-template-columns: minmax(0, 1fr); }
}

.fr__label {
  min-width: 0;
  padding-top: 11px;

  &--choice { padding-top: 12px; }
}

.fr__name {
  display: block;
  margin: 0;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.35;
  color: $fu-text;
}

.fr__help {
  margin-top: 3px;
  font-size: 12.5px;
  line-height: 1.45;
  color: $fu-neutral-500;
  overflow-wrap: anywhere;
}

.fr__field { min-width: 0; }

.fr__note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 7px;
  font-size: 12.5px;
  line-height: 1.45;
  color: $fu-neutral-500;

  &--amber { color: $fu-warning-text; }
  &--red { color: $fu-danger-text; }
}

.fr__note-icon { margin-top: 1px; }
.fr__note-body { min-width: 0; overflow-wrap: anywhere; }

.fr__note-extra { margin-top: 4px; }

.fr__codes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.fr__code {
  padding: 2px 7px;
  border: 1px solid $fu-border;
  border-radius: 6px;
  background: $fu-surface;
  font-family: $pp-font-mono;
  font-size: 12px;
  color: $fu-neutral-700;
}

// The sheet is the full viewport width at 900px and below, so this is "the sheet is under ~620px".
@media (max-width: 620px) {
  .fr { grid-template-columns: minmax(0, 1fr); gap: 4px; }
  .fr__label,
  .fr__label--choice { padding-top: 0; }
}
</style>
